import type { ResumeDto } from "@izicvs/dto";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { t } from "@lingui/macro";
import {
  CopySimple,
  DotsThreeVertical,
  FolderOpen,
  Lock,
  LockOpen,
  PencilSimple,
  TrashSimple,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import { useDialog } from "@/client/stores/dialog";

import { BaseCard } from "./base-card";
import { ResumePreviewFrame } from "./resume-preview-frame";

type Props = {
  resume: ResumeDto;
};

export const ResumeCard = ({ resume }: Props) => {
  const navigate = useNavigate();
  const { open } = useDialog<ResumeDto>("resume");
  const { open: lockOpen } = useDialog<ResumeDto>("lock");
  const lastUpdated = dayjs().to(resume.updatedAt);

  const onOpen = () => {
    if (resume.locked) {
      toast({
        variant: "info",
        title: t`This resume is locked.`,
        description: t`Unlock it to make changes.`,
      });
      return;
    }
    void navigate(`/builder/${resume.id}`);
  };

  const onUpdate = () => {
    open("update", { id: "resume", item: resume });
  };

  const onDuplicate = () => {
    open("duplicate", { id: "resume", item: resume });
  };

  const onLockChange = () => {
    lockOpen(resume.locked ? "update" : "create", { id: "lock", item: resume });
  };

  const onDelete = () => {
    open("delete", { id: "resume", item: resume });
  };

  return (
    <div className="group relative">
      <BaseCard
        className={cn("space-y-0", resume.locked ? "cursor-not-allowed" : "cursor-pointer")}
        onClick={onOpen}
      >
        <AnimatePresence>
          {resume.locked && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-y-2 bg-background/75 backdrop-blur-sm"
            >
              <Lock size={42} />
              <span className="text-sm font-medium">{t`Locked`}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div
          className={cn(
            "absolute inset-x-0 bottom-0 z-10 flex flex-col justify-end space-y-0.5 p-4 pt-12",
            "bg-gradient-to-t from-background/80 to-transparent",
          )}
        >
          <h4 className="line-clamp-2 font-medium">{resume.title}</h4>
          <p className="line-clamp-1 text-xs opacity-75">{t`Last updated ${lastUpdated}`}</p>
        </div>

        <ResumePreviewFrame resume={resume} />
      </BaseCard>
      {/* Quick actions: always visible on touch, reveal on hover on desktop. */}
      <div className="absolute right-2 top-2 z-20">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex size-9 items-center justify-center rounded-full bg-background/80 text-foreground shadow-sm backdrop-blur transition hover:bg-background sm:opacity-0 sm:group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation(); // Empêche l'événement de se propager à la carte
              }}
            >
              <DotsThreeVertical size={18} weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent sideOffset={5} align="end">
            <DropdownMenuItem onClick={onOpen}>
              <FolderOpen size={14} className="mr-2" />
              {t`Open`}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onUpdate}>
              <PencilSimple size={14} className="mr-2" />
              {t`Rename`}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <CopySimple size={14} className="mr-2" />
              {t`Duplicate`}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLockChange}>
              {resume.locked ? (
                <LockOpen size={14} className="mr-2" />
              ) : (
                <Lock size={14} className="mr-2" />
              )}
              {resume.locked ? t`Unlock` : t`Lock`}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-error" onClick={onDelete}>
              <TrashSimple size={14} className="mr-2" />
              {t`Delete`}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
